import React from 'react';
import './SPLandingPage.scss';
import { Box, Button, Tab, Tabs, Typography } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getAllRegistrationData } from '../../../redux/slices/sellerRegSlice';
import TranslateDate from '../TranslateDate';
import GetLabels from '../GetLabels';
import SPOnboarding from '../../../components/SellerProfile/OnboardingBox/SPOnboardingBox';
import profileIcon from '../../../assets/images/sellerProfileIcon/account-profile.svg';
import userIcon from '../../../assets/images/sellerProfileIcon/users.svg';
import notificationsIcon from '../../../assets/images/sellerProfileIcon/notification.svg';
import apiInfoIcon from '../../../assets/images/sellerProfileIcon/key.svg';
import settingsIcon from '../../../assets/images/sellerProfileIcon/settings.svg';
import jiraIcon from '../../../assets/images/sellerProfileIcon/jira.svg';
import { SvgIcon } from '../../SellerRegistration/SellerRegistrationForm';
import airCandaIcon from '../../../assets/images/sellerProfileIcon/red.svg';
import {ReactComponent as EditIcon} from '../../../assets/images/sellerProfileIcon/edit.svg';
import { iso2To3 } from '../../../constants/apiUtil';

// Legacy business-model values → current codes (matched by code or legacy display
// name): Online Travel Agency (OTA) → Travel Agency (AG); Corporate Booking Tool
// (CB) → Online Booking Tool (OB).
const BIZ_MODEL_LEGACY_MAP: Record<string, string> = {
  'OTA': 'AG',
  'Online Travel Agency': 'AG',
  'CB': 'OB',
  'Corporate Booking Tool': 'OB',
};

// Legacy (inactive) customer-type codes → display names. The API only returns
// active types, so a stored legacy code would otherwise render as a bare code.
const CUSTOMER_TYPE_LEGACY_MAP: Record<string, { en: string; fr: string }> = {
  AG: { en: 'Agency', fr: 'Agence' },
  ALL: { en: 'All', fr: 'All' },
  CR: { en: 'Consumer', fr: 'Consommateur' },
};

// Renders a field value, announcing the fallback i18n text to screen readers when empty
const FieldValue = ({ value }: { value: React.ReactNode }) => {
  const { t } = useTranslation();
  return (
    <>
      {value}
      {value === '—' && (
        <Box component="span" sx={visuallyHidden}>
          {t('registrationStepsData.common.fallbackEmptyAriaLabel')}
        </Box>
      )}
    </>
  );
};
 
function SPLandingPage() {
  const { compProfile, defaultCompData, spPageText, isAdmin, company_name } = useSelector(
    (state: any) => state.sellerProfile
  );
  const {
    pos: regPos,
    apiSchemas: regApiSchemas,
    businessModel: regBusinessModel,
    customerTypes: regCustomerTypes,
    supportedFlow: regSupportedFlow
  } = useSelector(
    (state: any) => state.sellerReg
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
 
  React.useEffect(() => {
      dispatch(getAllRegistrationData());
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
 
  window.scrollTo({ top: 0, behavior: 'smooth' });
 
  // ── Status helpers ─────────────────────────────────────────────────────────
  const registrationDate = compProfile?.registration_date || '';
  const formattedRegDate = TranslateDate(registrationDate, i18n.language);
 
  const currentStatus =
    i18n.language === 'fr' ? compProfile?.status_fr : compProfile?.status_en;
  const isBuilding =
    currentStatus === t('sellerProfile.dashboard.status.building');
  const isCertified =
    currentStatus === t('sellerProfile.dashboard.status.certified');
 
  // ── POS helpers ────────────────────────────────────────────────────────────
  const lang = i18n.language === 'fr' ? 'fr' : 'en';
 
  // Build a map: pos_code → country name using all available sources.
  const posCodeToName = React.useMemo(() => {
    const map: Record<string, string> = {};
 
    // 1. defaultCompData.pos_type — loaded with the profile
    (defaultCompData?.pos_type || []).forEach(({ pos_code, country, pays }: any) => {
      map[pos_code] = lang === 'fr' ? (pays || country) : (country || pays);
    });
 
    // 2. regPos — pos objects from registration state
    ((regPos?.[lang]) || []).forEach((entry: any) => {
      if (entry?.code && entry?.desc) map[entry.code] = entry.desc;
    });
 
    return map;
  }, [defaultCompData, regPos, lang]);
 
  const getPosCountryName = (posEntry: string): string => {
    const parts = posEntry.trim().split(' ');
    const code = parts[parts.length - 1];
    return posCodeToName[code] || (parts.length > 1 ? parts.slice(0, -1).join(' ') : posEntry);
  };
 
  const getPosCountryCode = (posEntry: string): string => {
    const parts = posEntry.trim().split(' ');
    const code = parts[parts.length - 1]; // 2-char ISO code
    return iso2To3[code] || ''; // 3-char code for flag image filename
  };
 
  // ── Supported flow helpers ──────────────────────────────────────────────────
  const supportedFlowIdToName = React.useMemo(() => {
    const map: Record<string, string> = {};
    ((regSupportedFlow?.[lang]) || []).forEach((sf: any) => {
      if (sf?.code && sf?.desc) {
        map[sf.code] = sf.desc;
      }
    });
    return map;
  }, [regSupportedFlow, lang]);
 
  const getSupportedFlowName = (id: string): string =>
    supportedFlowIdToName[id] || id;
 
  // ── Business model helpers ─────────────────────────────────────────────────
  const bizModelIdToName = React.useMemo(() => {
    const map: Record<string, string> = {};
    ((regBusinessModel?.[lang]) || []).forEach((bm: any) => {
      if (bm?.code && bm?.desc) {
        map[bm.code] = bm.desc;
      }
    });
    (defaultCompData?.business_model || []).forEach((bm: any) => {
      map[bm.business_model_id] = lang === 'fr'
        ? (bm.business_model_type_fr || bm.business_model_type)
        : bm.business_model_type;
    });
    return map;
  }, [regBusinessModel, defaultCompData, lang]);

  const getBizModelName = (code: string): string => {
    const canonical = BIZ_MODEL_LEGACY_MAP[code] || code;
    return bizModelIdToName[canonical] || code;
  };
 
  // ── Customer type helpers ──────────────────────────────────────────────────
  const customerTypeIdToName = React.useMemo(() => {
    const map: Record<string, string> = {};
    ((regCustomerTypes?.[lang]) || []).forEach((ct: any) => {
      if (ct?.code && ct?.desc) {
        map[ct.code] = ct.desc;
      }
    });
    (defaultCompData?.customer_types || []).forEach((ct: any) => {
      map[ct.customer_type_id] = lang === 'fr'
        ? (ct.customer_type_fr || ct.customer_type)
        : ct.customer_type;
    });
    return map;
  }, [regCustomerTypes, defaultCompData, lang]);
 
  const getCustomerTypeName = (code: string): string => {
    if (customerTypeIdToName[code]) return customerTypeIdToName[code];
    const legacy = CUSTOMER_TYPE_LEGACY_MAP[(code || '').toUpperCase()];
    if (legacy) return lang === 'fr' ? legacy.fr : legacy.en;
    return code;
  };

  // ── API schema helpers ────────────────────────────────────────────
  const apiSchemaCodeToName = React.useMemo(() => {
    const map: Record<string, string> = {};
    ((regApiSchemas?.[lang]) || []).forEach((s: any) => {
      if (s?.code && s?.desc) map[s.code] = s.desc;
    });
    (defaultCompData?.api_schema || []).forEach((s: any) => {
      if (s?.code && s?.api_schema_version) map[s.code] = s.api_schema_version;
    });
    return map;
  }, [regApiSchemas, defaultCompData, lang]);

  const getApiSchemaName = (code: string): string =>
    apiSchemaCodeToName[code] || code;
 
  // ── Tab navigation ─────────────────────────────────────────────────────────
  const handleTabChange = (_: React.SyntheticEvent, path: string) => {
    navigate(`/${i18n.language}${path}`);
  };
 
  const lbl = (key: string) => GetLabels(`spPageText.${key}`, spPageText);

  return (
    <main className="spLandingPage">
      {/* ── Company name heading ─────────────────────────────────────────── */}
      <Box className="spHeadingRow">
        <h1 className="spCompanyHeading">{company_name}</h1>
        {isAdmin && (
          <Button
            variant="contained"
            className="spEditProfileBtn spEditProfileBtnMobile spEditProfileBtnMobileIcon"
            startIcon={<EditIcon className="spEditIcon" />}
            aria-label={lbl('updateProfileDetails') || 'Edit profile'}
            onClick={() => navigate(`/${i18n.language}/seller-profile/profile`)}
          >
            <span className="spEditBtnLabel">{lbl('updateProfileDetails') || 'Edit profile'}</span>
          </Button>
        )}
      </Box>
 
      {/* ── Onboarding steps ─────────────────────────────────────────────── */}
      {isAdmin && <SPOnboarding />}
 
      {/* ── Tabs ──────────────────────────────────────────────────────────── */}
      <Box className="spProfileTabsBar">
        <Tabs
          value="/seller-profile"
          onChange={handleTabChange}
          className="spProfileTabs"
          variant="scrollable"
          scrollButtons={false}
          TabIndicatorProps={{ style: { height: '3px' } }}
        >
          <Tab label={lbl('profile') || 'Profile'} value="/seller-profile"
            icon={<img src={profileIcon} alt="Profile" className="tabIcon" />}
            iconPosition="start"
            />
          {isAdmin && (
            <Tab label={lbl('users')} value="/seller-profile/users"
              icon={<img src={userIcon} alt="Users" className="tabIcon" />}
              iconPosition="start"
            />
          )}
          {isAdmin && (
            <Tab
              label={lbl('notifications')}
              value="/seller-profile/notifications"
              icon={<img src={notificationsIcon} alt="Notifications" className="tabIcon" />}
              iconPosition="start"
            />
          )}
          {isAdmin && (
            <Tab
              label={lbl('apiContact')}
              value="/seller-profile/api-credentials"
              icon={<img src={apiInfoIcon} alt="API Info" className="tabIcon" />}
              iconPosition="start"
            />
          )}
          <Tab
            label={lbl('mySettings')}
            value="/seller-profile/user-settings"
            icon={<img src={settingsIcon} alt="Settings" className="tabIcon" />}
            iconPosition="start"
          />
        </Tabs>
      </Box>
 
      {/* ── Page body: main + sidebar ──────────────────────────────────────── */}
      <Box className="spProfileBody">
        {/* ── Left column ────────────────────────────────────────────────── */}
        <Box className="spProfileLeft">
        {/* ── Main content ───────────────────────────────────────────────── */}
        <Box className="spProfileMain">
          {/* Commercial details */}
          <Box className="spProfileSection">
            <Typography className="spSectionTitle" component="h2">
              {lbl('commercialDetails') || 'Commercial details'}
            </Typography>
            <Box className="spSectionGrid">
              <Box className="spFieldGrid">
                <SvgIcon src="/images/registration/AddressIcon.svg" size={24} color="#000000" className="spFieldIcon" />
                <Box>
                <Typography className="spFieldLabel">
                  {lbl('coAddress')}
                </Typography>
                <Typography className="spFieldValue">
                  <FieldValue value={compProfile?.company_address || '—'} />
                </Typography>
                </Box>
              </Box>
              <Box className="spFieldGrid">
                <SvgIcon src="/images/registration/websiteIcon.svg" size={24} color="#000000" className="spFieldIcon" />
                <Box>
                <Typography className="spFieldLabel">
                  {lbl('coWebsite')}
                </Typography>
                <Typography className="spFieldValue">
                  {compProfile?.company_website ? (
                    <a
                      href={compProfile.company_website}
                      target="_blank"
                      rel="noreferrer"
                      className="spWebsiteLink"
                    >
                      {compProfile.company_website}
                    </a>
                  ) : (
                    <FieldValue value="—" />
                  )}
                </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
 
          {/* Business model information */}
          <Box className="spProfileSection">
            <Box className="spSectionGrid spTwoCol">
              <Box className="spField">
                <Typography className="spFieldLabel">{lbl('coType')}</Typography>
                <Typography className="spFieldValue">
                  <FieldValue
                    value={
                      compProfile?.customer_type
                        ? getCustomerTypeName(compProfile.customer_type)
                        : '—'
                    }
                  />
                </Typography>
              </Box>
              <Box className="spField">
                <Typography className="spFieldLabel">
                  {lbl('bizModel')}
                </Typography>
                <Typography className="spFieldValue">
                  <FieldValue
                    value={
                      Array.isArray(compProfile?.business_model_type)
                        ? Array.from(new Set(compProfile.business_model_type.map((code: string) => getBizModelName(code)))).join(', ')
                        : compProfile?.business_model_type
                          ? getBizModelName(compProfile.business_model_type)
                          : '—'
                    }
                  />
                </Typography>
                </Box>
              </Box>
              <Box className="spFieldPos">
                <Typography className="spFieldLabel">
                  {lbl('acPOS') || 'POS implementation'}
                </Typography>
                <ul className="spPosFlags" aria-label={lbl('acPOS') || 'POS implementation'}>
                  {[...(compProfile?.pos_type || [])].sort((a: string, b: string) => {
                    const aCode = a.trim().split(' ').pop()?.toUpperCase() || '';
                    const bCode = b.trim().split(' ').pop()?.toUpperCase() || '';
                    const aIsOther = aCode === 'OT' || getPosCountryName(a).toLowerCase() === 'other';
                    const bIsOther = bCode === 'OT' || getPosCountryName(b).toLowerCase() === 'other';
                    if (aIsOther && !bIsOther) return 1;
                    if (!aIsOther && bIsOther) return -1;
                    return getPosCountryName(a).localeCompare(getPosCountryName(b));
                  }).map(
                    (pos: string, i: number) => {
                      const countryCode = getPosCountryCode(pos);
                      const countryName = getPosCountryName(pos);
                      const parts = pos.trim().split(' ');
                      const isoCode = parts[parts.length - 1];
                      const iataNumber = compProfile?.pos_iata_map?.[isoCode] || '';
                      const isMobileHidden = i >= 2;
                      return (
                        <li key={i} className={`spPosItem${isMobileHidden ? ' spPosItemHiddenMobile' : ''}`}>
                          {countryCode && (
                            <img
                              alt={countryName}
                              src={`/images/flags/${countryCode}.png`}
                              className="spPosFlag"
                            />
                          )}
                          <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                          }}>
                            <Typography className="spPosText">{countryName}</Typography>
                            {iataNumber && <Typography className="spIata">{iataNumber}</Typography>}
                          </Box>
                        </li>
                      );
                    }
                  )}
                  {(compProfile?.pos_type || []).length > 2 && (
                    <li className="spPosMoreBadge" aria-label={`${compProfile.pos_type.length - 2} more POS entries`}>
                      +{(compProfile.pos_type.length - 2)}
                    </li>
                  )}
                </ul>
              </Box>
          </Box>
 
          {/* Technical details */}
          <Box className="spProfileSection">
            <Typography className="spSectionTitle" component="h2">
              {lbl('technicalDetails') || 'Technical details'}
            </Typography>
            <Box className="spSectionGrid spThreeCol">
              <Box className="spField">
                <Typography className="spFieldLabel">
                  {lbl('impDate')}
                </Typography>
                <Typography className="spFieldValue">
                  <FieldValue value={compProfile?.implementation_start_date || '—'} />
                </Typography>
              </Box>
              <Box className="spField">
                <Typography className="spFieldLabel">
                  {lbl('supportedFlow') || 'Supported flow'}
                </Typography>
                <Typography className="spFieldValue">
                  <FieldValue
                    value={
                      compProfile?.support_flow
                        ? getSupportedFlowName(compProfile.support_flow)
                        : '—'
                    }
                  />
                </Typography>
              </Box>
              <Box className="spField">
                <Typography className="spFieldLabel">
                  {lbl('apiSchema') || 'API schema'}
                </Typography>
                <Typography className="spFieldValue">
                  <FieldValue
                    value={
                      Array.isArray(compProfile?.api_schema_version)
                        ? compProfile.api_schema_version
                            .map((code: string) => getApiSchemaName(code))
                            .join(', ')
                        : compProfile?.api_schema_version
                          ? getApiSchemaName(compProfile.api_schema_version)
                          : '—'
                    }
                  />
                </Typography>
              </Box>
            </Box>
          </Box>
 
        </Box>
 
        {/* Contact cards */}
        <Box className="spContactsRow">
            {/* Business contact */}
            <Box className="spContactsCard spContactCard">
              <Typography className="spContactTitle" component="h2">
                {lbl('businessContact')}
              </Typography>
              <Typography className="spContactName">
                <FieldValue
                  value={
                    `${compProfile?.business_contact_first_name || ''} ${compProfile?.business_contact_last_name || ''}`.trim() ||
                    '—'
                  }
                />
              </Typography>
              {compProfile?.business_phone_no && (
                <Box className="spContactDetail">
                  <SvgIcon src="/images/registration/phoneIcon.svg" size={16} color="#000000" className="spContactIcon" />
                  <Typography>{compProfile.business_phone_no}</Typography>
                </Box>
              )}
              {compProfile?.business_email && (
                <Box className="spContactDetail">
                  <SvgIcon src="/images/registration/emailIcon.svg" size={16} color="#000000" className="spContactIcon" />
                  <Typography>{compProfile.business_email}</Typography>
                </Box>
              )}
            </Box>
 
            {/* Admin contact */}
            <Box className="spContactsCard spContactCard">
              <Typography className="spContactTitle" component="h2">
                {lbl('adminContact')}
              </Typography>
              <Typography className="spContactName">
                <FieldValue
                  value={
                    `${compProfile?.admin_contact_first_name || ''} ${compProfile?.admin_contact_last_name || ''}`.trim() ||
                    '—'
                  }
                />
              </Typography>
              {compProfile?.admin_contact_phone && (
                <Box className="spContactDetail">
                  <SvgIcon src="/images/registration/phoneIcon.svg" size={16} color="#000000" className="spContactIcon" />
                  <Typography>{compProfile.admin_contact_phone}</Typography>
                </Box>
              )}
              {compProfile?.admin_contact_email && (
                <Box className="spContactDetail">
                  <SvgIcon src="/images/registration/emailIcon.svg" size={16} color="#000000" className="spContactIcon" />
                  <Typography>{compProfile.admin_contact_email}</Typography>
                </Box>
              )}
            </Box>
        </Box>
        </Box>
 
        {/* ── Right sidebar ──────────────────────────────────────────────── */}
        <Box className="spProfileSidebar">
          {/* Edit profile button */}
          {isAdmin && (
            <Button
              variant="contained"
              fullWidth
              className="spEditProfileBtn spEditProfileBtnDesktop"
              startIcon={<EditIcon className="spEditIcon" />}
              onClick={() =>
                navigate(`/${i18n.language}/seller-profile/profile`)
              }
            >
              {lbl('updateProfileDetails') || 'Edit profile'}
            </Button>
          )}
 
          {/* Status card */}
          <Box className="spSidebarCard spStatusCard">
            <Box className="spSidebarRow">
              <Typography className="spSidebarLabel">
                {lbl('registered') || 'Registered:'}
              </Typography>
              <Typography className="spSidebarDate">
                {formattedRegDate}
              </Typography>
            </Box>
 
            <Box className="spSidebarRow spSidebarStatus">
              <Typography className="spSidebarLabel">
                {lbl('currentStatus') || 'Current status:'}
              </Typography>
              <Typography className="spCurrentStatusValue">
                {currentStatus || '—'}
              </Typography>
            </Box>
            {!isCertified && (
              <>
                <Box className="spSidebarRow spSidebarStatus">
                  <Typography className="spSidebarLabel">
                    {lbl('nextStatus') || 'Next status:'}
                  </Typography>
                  <Box>
                    {!isBuilding && (
                      <Typography className="spNextStatusValue">
                        {t('sellerProfile.dashboard.building')}
                      </Typography>
                    )}
                    {isBuilding &&<Typography className="spNextStatusValue">
                      {t('sellerProfile.dashboard.certified')}
                    </Typography>}
                  </Box>
                </Box>
              </>
            )}
          </Box>
 
          {/* Your tools card */}
          <Box className="spSidebarCard spToolsCard">
            <Typography component="h2" className="spToolsTitle">
              {lbl('yourTools') || 'Your tools'}
            </Typography>
            <Box className="spToolItem">
              <Box className="spToolLink">
                <img src={jiraIcon} alt="Jira" className="spSidebarFieldIcon" />
                <a
                  href={process.env.REACT_APP_JIRA_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="spToolLinkText"
                >
                  {lbl('manageTickets') || 'Manage tickets'}
                  <OpenInNewIcon className="linkIcon" />
                </a>
              </Box>
              <Typography className="spToolDesc">
                {lbl('jiraDesc') || 'Access your Jira board'}
              </Typography>
            </Box>
            <Box className="spToolItem">
              <Box className="spToolLink">
                <img src={airCandaIcon} alt="Air Canada" className="spSidebarFieldIcon" />
                <a
                  href={process.env.REACT_APP_SANDBOX_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="spToolLinkText"
                >
                  {lbl('experienceNDC') || 'Experience NDC'}
                  <OpenInNewIcon className="linkIcon" />
                </a>
              </Box>
              <Typography className="spToolDesc">
                {lbl('sandboxDesc') || 'Test our latest features in Sandbox'}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </main>
  );
}
 
export default SPLandingPage;